import { Navigate, useLocation, useNavigate } from 'react-router';
import { Helmet } from 'react-helmet';
import { getHotelReservation } from '../apis/hotelApis';
import { getPageUrl, getQueryParams, safeCast } from '../utils/urlUtils';
import { object, number } from 'yup';
import { useContext, useMemo, useState } from 'react';
import { HotelContext } from '../modules/HotelContext';
import { Alert, Button, Container, Spinner } from 'react-bootstrap';
import IconBack from '../icons/IconBack';
import { StyledBreadcrumb } from './Review.styled';
import { StyledHotelName } from './Search.styled';
import { PaymentStatus } from '../types/hotelTypes';
import { StyledAlertTitle } from './Reservation.styled';
import { language } from '../common/constants';
import { useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { useScrollIntoViewIframe } from '../hooks/useScrollIntoView';
import { PageAction } from '../types/newrelicTypes';

const schema = object({
  hotelId: number().required(),
  reservationId: number().required(),
});

interface IVietQRReturnParams {
  hotelId: number;
  reservationId: number;
}
const RETRY_INTERVAL = 1000;
const MAX_RETRIES = 10;

export const VietQRReturn = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = getQueryParams(search);
  const parsedParams = safeCast<IVietQRReturnParams>(params, schema);
  const { hotelId, reservationId } = parsedParams;
  const { hotel } = useContext(HotelContext);
  const sessionId = useMemo(() => uuidv4(), []);
  const [count, setCount] = useState(0);

  useScrollIntoViewIframe();

  const { data: reservation, error } = useQuery({
    queryKey: [`getHotelReservation`, hotelId, reservationId, sessionId],
    queryFn: async () => {
      const attempt = count + 1;
      setCount(attempt);
      const result = await getHotelReservation(hotelId, reservationId);

      const attrs = {
        attempt,
        paymentStatus: result.PaymentStatus,
        paymentMethod: result.PaymentMethod,
        reservationId: result.ReservationRoomId,
        bookingNumber: result.ReservationNumber,
        totalPrice: result.TotalPrice,
      };
      window.newrelic?.addPageAction(PageAction.PaymentRetry, attrs);

      if (attempt >= MAX_RETRIES && result.PaymentStatus !== PaymentStatus.Paid) {
        window.newrelic?.noticeError(new Error(`Payment wait period timed out`), attrs);
      }

      return result;
    },
    refetchInterval: () => (count < MAX_RETRIES ? RETRY_INTERVAL : false),
  });

  const goBack = () => {
    navigate(getPageUrl(`/${hotelId}/reservation/${reservationId}`));
  };

  if (count < MAX_RETRIES) {
    return (
      <div style={{ textAlign: 'center', flex: 1, marginTop: '4rem' }}>
        <Spinner animation="border" variant="primary" />
        <div style={{ marginTop: '1rem' }}>Thanh toán đang được xử lý. Vui lòng đợi trong giây lát.</div>
      </div>
    );
  }

  if (reservation.PaymentStatus !== PaymentStatus.Paid) {
    return (
      <div className="text-center">
        <Alert variant="warning">
          <StyledAlertTitle>
            Chưa tìm thấy giao dịch thanh toán của bạn. <br />
          </StyledAlertTitle>
          Trường hợp tài khoản đã bị trừ tiền, vui lòng chat để được hỗ trợ.
          <hr />
          <div>
            <div>
              <span>Mã đặt phòng: </span>
              <b>#{reservation?.ReservationNumber}</b>
            </div>
            <div>
              <span>Khách sạn: </span>
              <b>{hotel.hotelName}</b>
            </div>
          </div>
        </Alert>
        <Button variant="link" onClick={goBack}>
          Quay lại trang chi tiết đặt phòng
        </Button>
      </div>
    );
  }

  if (error) {
    return <Alert>{language.GENERIC_ERROR}</Alert>;
  }

  return <Navigate to={getPageUrl(`/${hotelId}/reservation/${reservationId}`)} />;
};

export const PaymentReturnPage = () => {
  const navigate = useNavigate();
  const { hotel } = useContext(HotelContext);

  const { search } = useLocation();
  const params = getQueryParams(search);
  const parsedParams = safeCast<IVietQRReturnParams>(params, schema);

  if (!parsedParams) {
    return <Alert>{language.GENERIC_ERROR}</Alert>;
  }

  const { hotelId, reservationId } = parsedParams;
  const goBack = () => {
    navigate(getPageUrl(`/${hotelId}/reservation/${reservationId}`));
  };

  return (
    <div
      style={{
        flex: 1,
        width: '100%',
        backgroundColor: 'rgb(255, 255, 255)',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Container fluid>
        <Helmet>
          <title>{`Đang xử lý thanh toán - ${hotel.hotelName}}`}</title>
        </Helmet>
        <StyledBreadcrumb>
          <div className="left-breadcrumb">
            <Button size="sm" variant="link" onClick={goBack}>
              <IconBack />
            </Button>
            <StyledHotelName>{hotel.hotelName}</StyledHotelName>
          </div>
          <Button size="sm" variant="link" onClick={goBack}>
            Hủy thanh toán
          </Button>
        </StyledBreadcrumb>
        <div style={{ padding: '1rem', flex: 1 }}>
          <VietQRReturn />
        </div>
      </Container>
    </div>
  );
};
